import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../ui/SafeIcon'

const { FiTruck, FiShield, FiDollarSign, FiGlobe, FiHeadphones, FiClock } = FiIcons

function FeaturesSection() {
  const { t } = useTranslation()
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 })

  const features = [
    { icon: FiDollarSign, title: 'features.price.title', desc: 'features.price.desc', color: 'text-green-600', bgColor: 'bg-green-100' },
    { icon: FiTruck, title: 'features.customs.title', desc: 'features.customs.desc', color: 'text-blue-600', bgColor: 'bg-blue-100' },
    { icon: FiShield, title: 'features.protection.title', desc: 'features.protection.desc', color: 'text-purple-600', bgColor: 'bg-purple-100' },
    { icon: FiGlobe, title: 'features.lang.title', desc: 'features.lang.desc', color: 'text-orange-600', bgColor: 'bg-orange-100' },
    { icon: FiHeadphones, title: 'features.support.title', desc: 'features.support.desc', color: 'text-red-600', bgColor: 'bg-red-100' },
    { icon: FiClock, title: 'features.shipping.title', desc: 'features.shipping.desc', color: 'text-indigo-600', bgColor: 'bg-indigo-100' }
  ]

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {t('features.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('features.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="relative group"
            >
              <div className="bg-white rounded-xl border border-gray-200 p-8 h-full transition-all duration-300 group-hover:shadow-xl group-hover:border-gray-300">
                {/* Icon */}
                <div className={`w-16 h-16 ${feature.bgColor} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon icon={feature.icon} className={`w-8 h-8 ${feature.color}`} />
                </div>
                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {t(feature.title)}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t(feature.desc)}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              {t('features.cta.title')}
            </h3>
            <p className="text-blue-100 mb-6">
              {t('features.cta.subtitle')}
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              {t('features.cta.button')}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default FeaturesSection